/**
 * AuthContext
 *
 * Single source of truth for authentication state across the app.
 *
 * Responsibilities:
 *  - Subscribe to Supabase auth state changes
 *  - On every new session, fetch backend user data (profiles, subscription tier)
 *    via GET /users/me so the full user object is always available
 *  - Expose login / logout / register helpers that coordinate
 *    our backend API + Supabase session hydration
 *  - Expose `isAuthenticated`, `subscriptionTier`, and `activeProfile`
 *    as derived values for convenience
 *
 * DECISION: AuthContext intentionally replaces authStore.ts for new components.
 * The Zustand store remains for any legacy usage during the transition.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react'
import { supabase } from '@/lib/supabase'
import * as authApi from '@/api/auth'
import * as usersApi from '@/api/users'
import type { UserMe, Profile } from '@/api/users'
import type { SubscriptionTier } from '@/api/subscriptions'

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuthContextValue {
  /** Supabase session user — set immediately on auth state change */
  supabaseUser: ReturnType<typeof supabase.auth.getUser> extends Promise<{ data: { user: infer U } }>
    ? U | null
    : never

  /** Full backend user object including profiles and subscription — loaded async after session */
  backendUser: UserMe | null

  /** Convenience: active (non-deleted) profiles for this account */
  profiles: Profile[]

  /** Convenience: resolved subscription tier — falls back to 'free' */
  subscriptionTier: SubscriptionTier

  /** True once auth state has been determined (avoids flash of login page) */
  isLoading: boolean

  /** True when the session exists */
  isAuthenticated: boolean

  /** True when the user has admin role in Supabase app_metadata */
  isAdmin: boolean

  /** True while the backend user data is being fetched post-login */
  isUserLoading: boolean

  /** Any error that occurred during auth */
  error: string | null

  // ── Actions ──────────────────────────────────────────────────────────────

  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  register: (payload: Parameters<typeof authApi.register>[0]) => Promise<{
    message: string
    session_token: string | null
  }>
  verifyOtp: (phone: string, otp_code: string, full_name?: string) => Promise<void>

  /** Reload backend user data — call after updating profile, subscription, etc. */
  refreshUser: () => Promise<void>
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null)

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [supabaseUser, setSupabaseUser] = useState<AuthContextValue['supabaseUser']>(null)
  const [backendUser, setBackendUser] = useState<UserMe | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isUserLoading, setIsUserLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track in-flight backend fetch so rapid auth events don't race
  const fetchAbortRef = useRef<AbortController | null>(null)

  // ── Fetch backend user ──────────────────────────────────────────────────

  const fetchBackendUser = useCallback(async () => {
    // Cancel any in-flight fetch
    fetchAbortRef.current?.abort()
    fetchAbortRef.current = new AbortController()

    setIsUserLoading(true)
    try {
      const response = await usersApi.getMe()
      setBackendUser(response.data)
      setError(null)
    } catch {
      // If the backend fetch fails we still have a valid Supabase session.
      // Don't treat this as a full auth failure — the user is logged in,
      // just operating with degraded data. Individual pages can retry.
      setBackendUser(null)
    } finally {
      setIsUserLoading(false)
    }
  }, [])

  // ── Supabase auth state subscription ────────────────────────────────────

  useEffect(() => {
    let mounted = true

    // Get initial session without waiting for auth state change event
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return
      setSupabaseUser(session?.user ?? null)
      if (session?.user) {
        fetchBackendUser()
      }
      setIsLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return
      setSupabaseUser(session?.user ?? null)

      if (session?.user) {
        fetchBackendUser()
      } else {
        setBackendUser(null)
        setIsUserLoading(false)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
      fetchAbortRef.current?.abort()
    }
  }, [fetchBackendUser])

  // ── Actions ──────────────────────────────────────────────────────────────

  const login = useCallback(async (email: string, password: string) => {
    setError(null)
    try {
      await authApi.login(email, password)
      // Auth state change listener will call fetchBackendUser automatically
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed'
      setError(message)
      throw err
    }
  }, [])

  const logout = useCallback(async () => {
    setError(null)
    try {
      await authApi.logout()
    } finally {
      setBackendUser(null)
    }
  }, [])

  const register = useCallback(async (payload: Parameters<typeof authApi.register>[0]) => {
    setError(null)
    try {
      const response = await authApi.register(payload)
      return response
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed'
      setError(message)
      throw err
    }
  }, [])

  const verifyOtp = useCallback(async (phone: string, otp_code: string, full_name?: string) => {
    setError(null)
    try {
      await authApi.verifyOtp(phone, otp_code, full_name)
      // Auth state change listener will call fetchBackendUser automatically
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'OTP verification failed'
      setError(message)
      throw err
    }
  }, [])

  const refreshUser = useCallback(async () => {
    if (supabaseUser) {
      await fetchBackendUser()
    }
  }, [supabaseUser, fetchBackendUser])

  // ── Derived values ───────────────────────────────────────────────────────

  const isAuthenticated = !!supabaseUser
  const isAdmin = supabaseUser?.app_metadata?.['role'] === 'admin'
  const profiles = backendUser?.profiles ?? []
  const subscriptionTier: SubscriptionTier = backendUser?.subscription_tier ?? 'free'

  const value: AuthContextValue = {
    supabaseUser,
    backendUser,
    profiles,
    subscriptionTier,
    isLoading,
    isAuthenticated,
    isAdmin,
    isUserLoading,
    error,
    login,
    logout,
    register,
    verifyOtp,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>')
  }
  return ctx
}
